/**
 * AK-938 — «Закрыть опыт» (low-fi 5·А): с урожайностью по вариантам или как
 * недоделанный с причиной. Оба закрытия конечные (ADR-086 D9).
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCompanyOpTypes } from '@/components/operations/operationsApi';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { scienceKeys } from '@/lib/science/api';
import {
  findHarvestType,
  INCOMPLETE_REASONS,
  incompleteNote,
  planHarvest,
} from '@/lib/science/closeTrial';
import { formatCHa, variantMarks } from '@/lib/science/labels';
import type { TrialSummary } from '@/lib/science/types';

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

interface Props {
  trial: TrialSummary;
  fieldLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CloseTrialSheet({ trial, fieldLabel, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const opTypes = useCompanyOpTypes();
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [harvestDate, setHarvestDate] = useState(todayIso());
  const [reason, setReason] = useState<string>('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<'yield' | 'incomplete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const marks = variantMarks(trial.variants.map((v) => v.variant_role));
  const base = `/api/v1/fields/${trial.field_id}/trials/${trial.id}`;

  async function refresh() {
    await qc.invalidateQueries({ queryKey: scienceKeys.all });
  }

  async function closeWithYield() {
    setError(null);
    const plan = planHarvest(trial.variants, typed);
    if (!plan.ok) {
      setErrors(plan.errors);
      return;
    }
    const harvest = findHarvestType(opTypes.data ?? []);
    if (plan.ops.length && !harvest) {
      setError('В типах операций компании нет «Уборки» — заведите его в справочнике операций.');
      return;
    }
    setBusy('yield');
    let written = 0;
    try {
      for (const op of plan.ops) {
        await api.post(`/api/v1/fields/${trial.field_id}/operations`, {
          field_id: trial.field_id,
          company_op_type_id: harvest!.id,
          name: `Уборка · ${op.label}`,
          subzone_id: op.subzone_id,
          planned_start_date: harvestDate,
          actual_start_date: harvestDate,
          actual_end_date: harvestDate,
          actual_area_ha: op.area_ha,
          lifecycle_state: 'done',
          attrs: { harvested_yield_t: op.harvested_t },
        });
        written += 1;
      }
      await api.post(`${base}/close`, { outcome: 'yield' });
      await refresh();
      toast.success('Опыт закрыт с урожайностью');
      onOpenChange(false);
    } catch (e) {
      await refresh();
      const detail = e instanceof ApiError ? e.detail : 'неизвестная ошибка';
      setError(
        written
          ? `Уборка записана по ${written} из ${plan.ops.length} вариантов, опыт не закрыт: ${detail}`
          : `Опыт не закрыт: ${detail}`,
      );
    } finally {
      setBusy(null);
    }
  }

  async function closeIncomplete() {
    setError(null);
    const note = incompleteNote(reason, comment);
    if (!note) {
      setErrors({ reason: 'Укажите причину: почему опыт не доделан' });
      return;
    }
    setBusy('incomplete');
    try {
      await api.post(`${base}/close`, { outcome: 'incomplete', note });
      await refresh();
      toast.success('Опыт закрыт как недоделанный');
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Не удалось закрыть опыт');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Закрыть опыт</SheetTitle>
          <SheetDescription>{fieldLabel} · урожайность по вариантам</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 pt-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription role="alert">{error}</AlertDescription>
            </Alert>
          )}
          <div className="text-sm font-semibold">Уборку провели</div>
          {trial.variants.map((v, i) => (
            <div key={v.subzone_id} className="flex flex-col gap-1">
              <div className="grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-2">
                <Label htmlFor={`y-${v.subzone_id}`} className="font-normal">
                  {marks[i]} · {v.label}
                </Label>
                {v.yield_c_ha != null ? (
                  <span className="text-right text-sm">{formatCHa(v.yield_c_ha)} ц/га</span>
                ) : (
                  <Input
                    id={`y-${v.subzone_id}`}
                    inputMode="decimal"
                    placeholder="ц/га"
                    value={typed[v.subzone_id] ?? ''}
                    aria-invalid={!!errors[v.subzone_id] || undefined}
                    className={errors[v.subzone_id] ? 'border-destructive' : undefined}
                    onChange={(e) => {
                      setTyped({ ...typed, [v.subzone_id]: e.target.value });
                      setErrors({});
                    }}
                  />
                )}
              </div>
              {errors[v.subzone_id] && (
                <p role="alert" className="text-xs text-destructive">
                  {errors[v.subzone_id]}
                </p>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <Label htmlFor="harvest-date">Дата уборки</Label>
            <Input
              id="harvest-date"
              type="date"
              value={harvestDate}
              max={todayIso()}
              onChange={(e) => setHarvestDate(e.target.value)}
            />
          </div>
          <Button disabled={!!busy} onClick={() => void closeWithYield()}>
            {busy === 'yield' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Закрыть с урожайностью
          </Button>

          <Separator />

          <div className="text-sm font-semibold">Уборку не провели</div>
          <div className="flex flex-col gap-2">
            <Label>Причина</Label>
            <Select
              value={reason}
              onValueChange={(v) => {
                setReason(v);
                setErrors({});
              }}
            >
              <SelectTrigger aria-label="Причина" aria-invalid={!!errors.reason || undefined}>
                <SelectValue placeholder="Выберите причину" />
              </SelectTrigger>
              <SelectContent>
                {INCOMPLETE_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="incomplete-comment">Комментарий</Label>
            <Input
              id="incomplete-comment"
              placeholder="что случилось"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setErrors({});
              }}
            />
          </div>
          {errors.reason && (
            <p role="alert" className="text-xs text-destructive">
              {errors.reason}
            </p>
          )}
          <Button variant="outline" disabled={!!busy} onClick={() => void closeIncomplete()}>
            {busy === 'incomplete' && <Loader2 className="h-4 w-4 animate-spin" />}
            Закрыть как недоделанный
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
